(() => {
  if (customElements.get('purefect-finishing-touches')) return;

  class PurefectFinishingTouches extends HTMLElement {
    constructor() {
      super();
      this.handleChange = this.handleChange.bind(this);
      this.handleDetailsToggle = this.handleDetailsToggle.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.handleChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }

      this.cacheElements();
      this.outerWasOpen = Boolean(this.details?.open);
      this.addEventListener('toggle', this.handleDetailsToggle, true);
      this.syncState();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('toggle', this.handleDetailsToggle, true);

      this.unsubscribeVariantChange?.();
    }

    cacheElements() {
      this.details = this.querySelector('[data-pm-finishing-details]');
      this.includedDetails = this.querySelector('[data-pm-included-details]');
      this.summaryText = this.querySelector('[data-pm-finishing-summary]');
      this.propertyInput = this.querySelector('[data-pm-finishing-touches-property]');
      this.liveRegion = this.querySelector('[data-pm-finishing-live]');
      this.inputs = Array.from(this.querySelectorAll('[data-pm-finishing-touch]'));
    }

    handleDetailsToggle(event) {
      if (event.target !== this.details) return;

      const isOpen = Boolean(this.details.open);
      if (isOpen && !this.outerWasOpen && this.includedDetails) {
        this.includedDetails.open = true;
      }
      this.outerWasOpen = isOpen;
    }

    handleChange(event) {
      const input = event.target.closest?.('[data-pm-finishing-touch]');
      if (!input || !this.contains(input)) return;

      this.syncState();
      this.announce(input);
      this.dispatchSelectionChange();
    }

    getSelections() {
      return this.inputs
        .filter((input) => input.checked && !input.disabled)
        .map((input) => ({
          id: Number(input.value),
          label: input.dataset.touchLabel || '',
          priceCents: Number(input.dataset.touchPriceCents || 0),
        }));
    }

    syncState() {
      const selections = this.getSelections();
      const labels = selections.map((selection) => selection.label).filter(Boolean);

      this.inputs.forEach((input) => {
        input.closest('.pm-finishing-touches__option')?.classList.toggle('is-selected', input.checked);
      });

      if (this.propertyInput) {
        this.propertyInput.value = labels.join(', ');
        this.propertyInput.disabled = selections.length === 0;
      }

      this.updateSummary(selections);
      this.dataset.selectedCount = String(selections.length);
      this.dataset.selectedPriceCents = String(
        selections.reduce((total, selection) => total + selection.priceCents, 0)
      );
    }

    updateSummary(selections) {
      if (!this.summaryText) return;

      const includedCount = Number(this.dataset.includedCount || 0);
      const availableCount = Number(this.dataset.availableCount || 0);
      const hasPairing = this.dataset.hasPairing === 'true';
      const selectedTotal = selections.reduce((total, selection) => total + selection.priceCents, 0);
      const parts = [];

      if (hasPairing) parts.push('Pairing included');
      if (includedCount > 0) parts.push(`${includedCount} included`);

      if (selections.length > 0) {
        const noun = selections.length === 1 ? 'extra' : 'extras';
        parts.push(`${selections.length} ${noun} (+${this.formatMoney(selectedTotal)})`);
      } else if (availableCount > 0) {
        parts.push(parts.length ? 'Add extras' : 'Optional extras');
      }

      this.summaryText.textContent = parts.join(' · ');
    }

    formatMoney(cents) {
      const locale = this.dataset.locale || document.documentElement.lang || 'en-US';
      const currency = this.dataset.currency || 'USD';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      } catch {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    announce(input) {
      if (!this.liveRegion) return;

      const label = input.dataset.touchLabel || 'Finishing touch';
      const action = input.checked ? 'added' : 'removed';
      const selections = this.getSelections();
      const selectedTotal = selections.reduce((total, selection) => total + selection.priceCents, 0);
      const totalCopy = selections.length ? ` Extras total ${this.formatMoney(selectedTotal)}.` : '';

      this.liveRegion.textContent = '';
      window.requestAnimationFrame(() => {
        this.liveRegion.textContent = `${label} ${action}.${totalCopy}`;
      });
    }

    dispatchSelectionChange() {
      const selections = this.getSelections();
      this.dispatchEvent(
        new CustomEvent('pm:finishing-touches:change', {
          bubbles: true,
          detail: {
            selections,
            priceCents: selections.reduce((total, selection) => total + selection.priceCents, 0),
          },
        })
      );
    }

    handleVariantChange({ data } = {}) {
      const productInfo = this.closest('product-info');
      const sourceProductInfo = data?.html?.querySelector?.('product-info');
      if (!productInfo || !sourceProductInfo) return;
      if (String(productInfo.dataset.productId) !== String(sourceProductInfo.dataset.productId)) return;

      const activeSection = productInfo.dataset.originalSection || productInfo.dataset.section;
      if (data.sectionId && String(data.sectionId) !== String(activeSection)) return;

      const source = sourceProductInfo.querySelector('[data-pm-finishing-touches]');
      if (!source) return;

      const wasOpen = Boolean(this.details?.open);
      const hadIncludedDetails = Boolean(this.includedDetails);
      const includedWasOpen = Boolean(this.includedDetails?.open);
      const selectedIds = new Set(this.getSelections().map((selection) => String(selection.id)));
      const sourceSection = source.dataset.sectionId;
      const destinationSection = this.dataset.sectionId;
      let updatedMarkup = source.innerHTML;

      if (sourceSection && destinationSection && sourceSection !== destinationSection) {
        updatedMarkup = updatedMarkup.replaceAll(sourceSection, destinationSection);
      }

      this.innerHTML = updatedMarkup;
      this.hidden = source.hidden;
      this.dataset.baseVariantId = source.dataset.baseVariantId || '';
      this.dataset.includedCount = source.dataset.includedCount || '0';
      this.dataset.availableCount = source.dataset.availableCount || '0';
      this.dataset.hasPairing = source.dataset.hasPairing || 'false';
      this.dataset.currency = source.dataset.currency || this.dataset.currency || 'USD';
      this.dataset.locale = source.dataset.locale || this.dataset.locale || document.documentElement.lang || 'en-US';

      this.cacheElements();
      this.inputs.forEach((input) => {
        input.checked = !input.disabled && selectedIds.has(String(input.value));
      });
      this.outerWasOpen = wasOpen;
      if (this.details) this.details.open = wasOpen;
      if (this.includedDetails) {
        this.includedDetails.open = hadIncludedDetails ? includedWasOpen : wasOpen;
      }
      this.syncState();
      this.dispatchSelectionChange();
    }
  }

  customElements.define('purefect-finishing-touches', PurefectFinishingTouches);
})();
